import { useState, type CSSProperties } from 'react';
import type {
  TaskCodeWithCurrentRate,
  TimesheetEntryWithTaskCode,
  TimesheetWithEntries,
} from '@renewal/types';
import { blockPalette } from './blockColors.js';
import { isCompactBlock, visibleLines } from './blockLayout.js';
import { formatTime } from './timeFormat.js';
import './TimeBlock.css';

/**
 * What the week grid reads from an entry. Narrower than
 * `TimesheetEntryWithTaskCode` on purpose: the supervisor review payload sends
 * no `rateId` and only some task-code fields, and the grid needs neither. Any
 * full entry satisfies this, so the employee, volunteer and review screens all
 * pass theirs unchanged.
 */
export type GridEntry = Omit<TimesheetEntryWithTaskCode, 'rateId' | 'taskCode'> & {
  taskCode: Pick<TaskCodeWithCurrentRate, 'code' | 'name' | 'currentRate'>;
};

/** A timesheet whose entries satisfy {@link GridEntry}. */
export type GridTimesheet = Omit<TimesheetWithEntries, 'entries'> & { entries: GridEntry[] };

interface TimeBlockProps {
  entry: GridEntry;
  dayIndex: number;
  dayWidth: number;
  hourHeight: number;
  startHour: number;
  isSelected?: boolean;
  isFocused?: boolean;
  tabIndex?: number;
  onSelect?: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onResizeStart?: (edge: 'top' | 'bottom') => void;
  onKeyDown?: (e: React.KeyboardEvent, entryId: string) => void;
  disabled?: boolean;
}

export function TimeBlock({
  entry,
  dayIndex,
  dayWidth,
  hourHeight,
  startHour,
  isSelected,
  isFocused,
  tabIndex = 0,
  onSelect,
  onEdit,
  onDelete,
  onResizeStart,
  onKeyDown,
  disabled,
}: TimeBlockProps) {
  const [showActions, setShowActions] = useState(false);

  // Handle click - select first, then edit on double-click or action button
  const handleClick = (e: React.MouseEvent) => {
    if (disabled) return;
    e.stopPropagation();

    if (isSelected) {
      // Already selected, open edit
      onEdit();
    } else {
      // Select this block
      onSelect?.();
    }
  };

  // Handle resize handle mousedown
  const handleResizeMouseDown = (edge: 'top' | 'bottom') => (e: React.MouseEvent) => {
    if (disabled) return;
    e.stopPropagation();
    e.preventDefault();
    onResizeStart?.(edge);
  };

  // Calculate position from entry times
  const [startH, startM] = entry.startTime.split(':').map(Number);
  const [endH, endM] = entry.endTime.split(':').map(Number);
  const startMinutes = (startH ?? 0) * 60 + (startM ?? 0);
  const endMinutes = (endH ?? 0) * 60 + (endM ?? 0);

  const top = ((startMinutes - startHour * 60) / 60) * hourHeight;
  const height = ((endMinutes - startMinutes) / 60) * hourHeight;
  const left = dayIndex * dayWidth + 2; // 2px padding
  const width = dayWidth - 4; // 4px total padding

  // Stripe in the task code's own colour, light tint of it behind the text.
  const palette = blockPalette(entry.taskCodeId);
  const lines = visibleLines(height);

  // Calculate hours
  const hours = parseFloat(entry.hours);

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete();
  };

  // Build accessible label for screen readers
  const accessibleLabel = `${entry.taskCode.name}, ${formatTime(entry.startTime)} to ${formatTime(entry.endTime)}, ${hours.toFixed(1)} hours${entry.notes ? `, Notes: ${entry.notes}` : ''}`;

  return (
    <div
      className={`time-block ${disabled ? 'disabled' : ''} ${isSelected ? 'selected' : ''} ${isFocused ? 'focused' : ''} ${isCompactBlock(height) ? 'time-block--compact' : ''}`}
      style={
        {
          top: `${top}px`,
          height: `${Math.max(height, 20)}px`,
          left: `${left}px`,
          width: `${width}px`,
          backgroundColor: palette.fill,
          color: palette.text,
          borderLeftColor: palette.stripe,
          '--block-text-secondary': palette.secondaryText,
        } as CSSProperties
      }
      onClick={handleClick}
      // A press inside a block (body, edit/delete buttons) must not bubble to
      // the grid's onMouseDown, which would start a new drag selection under
      // the cursor and swallow the button click. Resize handles keep their own
      // mousedown handling below.
      onMouseDown={(e) => e.stopPropagation()}
      onKeyDown={(e) => onKeyDown?.(e, entry.id)}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
      title={`${entry.taskCode.name}\n${formatTime(entry.startTime)} - ${formatTime(entry.endTime)}\n${hours.toFixed(1)} hours\n$${entry.taskCode.currentRate.toFixed(2)}/hr${entry.notes ? `\n---\n${entry.notes}` : ''}`}
      tabIndex={disabled ? -1 : tabIndex}
      role="button"
      aria-label={accessibleLabel}
      aria-pressed={isSelected}
      data-testid={`time-block-${entry.id}`}
      // The submit error panel's "View affected entry" finds the block by this.
      data-entry-id={entry.id}
    >
      {/* Resize handles - only show when selected and not disabled */}
      {isSelected && !disabled && (
        <>
          <div
            className="resize-handle resize-handle-top"
            onMouseDown={handleResizeMouseDown('top')}
            data-testid={`time-block-resize-top-${entry.id}`}
          />
          <div
            className="resize-handle resize-handle-bottom"
            onMouseDown={handleResizeMouseDown('bottom')}
            data-testid={`time-block-resize-bottom-${entry.id}`}
          />
        </>
      )}
      <div className="block-content">
        <span className="block-task-code">{entry.taskCode.code}</span>
        {lines.name && <span className="block-task-name">{entry.taskCode.name}</span>}
        {lines.times && (
          <span className="block-times">
            {formatTime(entry.startTime)} - {formatTime(entry.endTime)}
          </span>
        )}
        {lines.hours && <span className="block-hours">{hours.toFixed(1)}h</span>}
      </div>

      {/* Action buttons on hover */}
      {showActions && !disabled && (
        <div className="block-actions">
          <button
            className="block-action-btn edit"
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            title="Edit"
            data-testid={`time-block-edit-${entry.id}`}
          >
            ✎
          </button>
          <button
            className="block-action-btn delete"
            onClick={handleDeleteClick}
            title="Delete"
            data-testid={`time-block-delete-${entry.id}`}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}
